const COMPONENT_LOADER_STATE_KEY = "__BCL_COMPONENTS_STATE__";

const loaderState = (window[COMPONENT_LOADER_STATE_KEY] = window[COMPONENT_LOADER_STATE_KEY] || {
  componentLoadStatusByName: Object.create(null),
  pendingImportPromiseByScopeKey: new Map(),
  observedDomScopes: new Set(),
  selectorCacheByComponentName: new Map(),
  scheduledElementsByComponentName: new Map(),
  lastImportCompletedAtByName: Object.create(null),
  deferredScanTimerByKey: new Map(),
  ioByKey: new Map(),
  rootIdByNode: new WeakMap(),
  rootIdSeq: 0,
  traceSequence: 0,
});

const { componentLoadStatusByName, pendingImportPromiseByScopeKey, observedDomScopes, selectorCacheByComponentName, scheduledElementsByComponentName, lastImportCompletedAtByName, deferredScanTimerByKey } = loaderState;

// ─── Debug ───────────────────────────────────────────────────────────────────

const isDebugEnabled = () => window.bclcomponents?.debug === true || window.bcl?.debug?.components === true;

const TRACE_TITLE_STYLE = "font-weight:bold;font-size:11px;color:#333;background:#e0e0e0;padding:2px 6px;border-radius:3px;";

const createTraceReport = (traceTag) => {
  const report = {
    loaded: [],
    skipped: [],
    failed: [],
    timingMs: Object.create(null),
    startedAt: performance.now(),
  };

  report.addLoaded = (componentName, ms) => {
    report.loaded.push({ Component: componentName, "Time (ms)": parseFloat((ms || 0).toFixed(2)) });
    report.timingMs[componentName] = ms || 0;
  };
  report.addSkipped = (componentName, reason) => report.skipped.push({ Component: componentName, Reason: reason });
  report.addFailed = (componentName, reason) => report.failed.push({ Component: componentName, Error: reason });

  report.print = (phaseLabel, options) => {
    if (!isDebugEnabled()) {
      return;
    }

    const { loaded, skipped, failed, timingMs } = report;
    const elapsed = (performance.now() - report.startedAt).toFixed(2);
    const icon = failed.length ? "❌" : loaded.length ? "✅" : "ℹ️";
    const summary = `${icon} [${traceTag}] ${phaseLabel} | Loaded: ${loaded.length} | Skipped: ${skipped.length} | Failed: ${failed.length} | Total: ${elapsed}ms`;

    console.groupCollapsed(`%c${summary}`, TRACE_TITLE_STYLE);

    if (options) {
      console.log("⚙️ Options:", options);
    }
    if (loaded.length) {
      console.groupCollapsed(`✅ Loaded (${loaded.length})`);
      console.table(loaded);
      console.groupEnd();
    }
    if (skipped.length) {
      console.groupCollapsed(`⏩ Skipped (${skipped.length})`);
      console.table(skipped);
      console.groupEnd();
    }
    if (failed.length) {
      console.group(`❌ Failed (${failed.length})`);
      console.table(failed);
      console.groupEnd();
    }

    const slowestImports = Object.entries(timingMs)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([componentName, ms]) => ({ Component: componentName, "Time (ms)": parseFloat(ms.toFixed(2)) }));

    if (slowestImports.length) {
      console.groupCollapsed("🐢 Slowest (Top 10)");
      console.table(slowestImports);
      console.groupEnd();
    }

    console.groupEnd();
  };

  report.reset = () => {
    report.loaded = [];
    report.skipped = [];
    report.failed = [];
    report.timingMs = Object.create(null);
    report.startedAt = performance.now();
  };

  return report;
};

const createLoadTrace = (options) => {
  if (!isDebugEnabled()) {
    return null;
  }

  const label = typeof options?.debugLabel === "string" && options.debugLabel ? options.debugLabel : "ALL";
  const traceTag = `${label}#${++loaderState.traceSequence}`;

  const initReport = createTraceReport(traceTag);
  const lateReport = createTraceReport(traceTag);

  let phase = "init";
  let lateDebounceTimer = null;

  const currentReport = () => (phase === "init" ? initReport : lateReport);

  const flushLateReport = () => {
    if (!lateReport.loaded.length && !lateReport.skipped.length && !lateReport.failed.length) {
      return;
    }
    lateReport.print("LATE LOAD", null);
    lateReport.reset();
  };

  const trace = {
    skip: (componentName, reason) => {
      currentReport().addSkipped(componentName, reason);
      if (phase === "done") {
        flushLateReport();
      }
    },
    loaded: (componentName, ms) => {
      currentReport().addLoaded(componentName, ms || 0);
      if (phase === "done") {
        clearTimeout(lateDebounceTimer);
        lateDebounceTimer = setTimeout(flushLateReport, 200);
      }
    },
    failed: (componentName, reason) => {
      currentReport().addFailed(componentName, reason || "Unknown");
      if (phase === "done") {
        flushLateReport();
      }
    },
    importDone: (componentName, ok, ms) => (ok ? trace.loaded(componentName, ms) : trace.failed(componentName, "Import Failed")),
    finish: () => {
      phase = "done";
      initReport.print("INITIALIZATION", options);
    },
  };

  return trace;
};

// ─── Browser Helpers ─────────────────────────────────────────────────────────

const yieldToBrowser = () => {
  if (typeof window.scheduler?.yield === "function") {
    return window.scheduler.yield();
  }
  if (typeof window.requestIdleCallback === "function") {
    return new Promise((resolve) => window.requestIdleCallback(resolve, { timeout: 200 }));
  }
  return new Promise((resolve) => window.requestAnimationFrame(resolve));
};

const waitMs = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const didExceedTimeBudget = (loopStartedAt, budgetMs) => performance.now() - loopStartedAt >= budgetMs;

const setManifestEntry = (componentName, ok) => {
  if (!window.bcl?.c) {
    return;
  }
  window.bcl.c.manifest = window.bcl.c.manifest || {};
  window.bcl.c.manifest[componentName] = ok;
};

// ─── Root / Scope Helpers ────────────────────────────────────────────────────

const getRootId = (root) => {
  let id = loaderState.rootIdByNode.get(root);
  if (!id) {
    id = `r${++loaderState.rootIdSeq}`;
    loaderState.rootIdByNode.set(root, id);
  }
  return id;
};

const getRootKey = (root) => (root === document ? "doc" : getRootId(root));
const buildScopeKey = (componentName, root) => `${componentName}::${getRootKey(root)}`;

const hasPendingImport = (componentName, root) => pendingImportPromiseByScopeKey.has(buildScopeKey(componentName, root));
const wasRecentlyImported = (componentName, windowMs = 250) => performance.now() - (lastImportCompletedAtByName[componentName] || 0) < windowMs;

const getScheduledElementsSet = (componentName) => {
  if (!scheduledElementsByComponentName.has(componentName)) {
    scheduledElementsByComponentName.set(componentName, new WeakSet());
  }
  return scheduledElementsByComponentName.get(componentName);
};

const markImportResult = (componentName, ok) => {
  componentLoadStatusByName[componentName] = ok;
  setManifestEntry(componentName, ok);
  lastImportCompletedAtByName[componentName] = performance.now();
};

// ─── Selector / Margin ───────────────────────────────────────────────────────

const normalizeSelector = (selector) => {
  if (!selector) {
    return "";
  }
  if (Array.isArray(selector)) {
    return selector.filter((s) => s && typeof s === "string").join(", ");
  }
  return typeof selector === "string" ? selector : "";
};

const SWIPER_INTENT_SELECTOR = '.swiper, [data-swiper], [class*="swiper"], [class*="gallery"], [class*="carousel"]';

const isSwiperIntentElement = (element) => {
  if (!element?.matches || !element?.querySelector) {
    return false;
  }
  return element.matches(SWIPER_INTENT_SELECTOR) || Boolean(element.querySelector(`${SWIPER_INTENT_SELECTOR}, .swiper-wrapper`));
};

const isMobileViewport = () => typeof window.matchMedia === "function" && window.matchMedia("(max-width: 768px)").matches;

const computePreloadMargin = (options) => {
  if (Number.isFinite(options?.preloadMargin)) {
    return options.preloadMargin;
  }

  const isMobile = isMobileViewport();
  const viewportHeight = Math.max(document.documentElement?.clientHeight || 0, window.innerHeight || 0);
  let margin = isMobile ? Math.max(180, Math.min(320, Math.round(viewportHeight * 0.3))) : 320;

  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (connection?.saveData) {
    margin = 0;
  } else if (/^(slow-2g|2g|3g)$/.test(connection?.effectiveType)) {
    margin = Math.max(margin, isMobile ? 360 : 480);
  }

  return Math.max(isMobile ? 0 : 50, Math.min(600, margin));
};

const getSelectorForComponent = (componentName, config) => {
  if (selectorCacheByComponentName.has(componentName)) {
    return selectorCacheByComponentName.get(componentName);
  }
  if (typeof config?.componentClass !== "function") {
    return "";
  }

  try {
    const selector = normalizeSelector(config.componentClass()?.selector);
    if (selector) {
      selectorCacheByComponentName.set(componentName, selector);
    }
    return selector || "";
  } catch {
    return "";
  }
};

// ─── Shared IntersectionObserver ─────────────────────────────────────────────

const getSharedIntersectionObserver = (root, preloadMargin) => {
  const ioKey = `${getRootKey(root)}::${preloadMargin}`;
  if (loaderState.ioByKey.has(ioKey)) {
    return loaderState.ioByKey.get(ioKey);
  }

  const ioRecord = { count: 0, callbacks: new WeakMap(), io: null };

  ioRecord.io = new IntersectionObserver(
    (entries) => {
      for (const { isIntersecting, target } of entries) {
        if (!isIntersecting) {
          continue;
        }

        const elementCallbacks = ioRecord.callbacks.get(target);
        ioRecord.callbacks.delete(target);
        ioRecord.io.unobserve(target);

        if (!elementCallbacks?.length) {
          continue;
        }
        ioRecord.count -= elementCallbacks.length;
        elementCallbacks.forEach((callback) => {
          try {
            callback();
          } catch (err) {
            console.error("[ComponentsLoader] IO callback error:", err);
          }
        });
      }

      if (ioRecord.count <= 0) {
        ioRecord.io.disconnect();
        loaderState.ioByKey.delete(ioKey);
      }
    },
    { root: root === document ? null : root, rootMargin: `${preloadMargin}px`, threshold: 0 },
  );

  loaderState.ioByKey.set(ioKey, ioRecord);
  return ioRecord;
};

// ─── Import ──────────────────────────────────────────────────────────────────

const importComponentNow = async (componentName, config, options, maxRetries, trace) => {
  const root = options?.root || document;
  const scopeKey = buildScopeKey(componentName, root);

  const isPresentFailing = options?.ignoreIsPresent !== true && typeof config?.isPresent === "function" && !config.isPresent(options);

  if (!config) {
    const globalFallback = window.bcl?.c?.[componentName];
    if (typeof globalFallback?.importComponent === "function") {
      return importComponentNow(componentName, globalFallback, options, 1, trace);
    }
    trace?.skip(componentName, "Missing Component Config");
    markImportResult(componentName, false);
    return false;
  }

  if (isPresentFailing && options?.root === document) {
    const globalFallback = window.bcl?.c?.[componentName];
    if (typeof globalFallback?.importComponent === "function") {
      return importComponentNow(componentName, globalFallback, options, 1, trace);
    }
    trace?.skip(componentName, "isPresent check failed");
    markImportResult(componentName, false);
    return false;
  }

  const pendingPromise = pendingImportPromiseByScopeKey.get(scopeKey);
  if (pendingPromise) {
    return pendingPromise;
  }

  const retries = Number.isFinite(maxRetries) && maxRetries > 0 ? maxRetries : 1;

  const importTask = (async () => {
    for (let attempt = 1; attempt <= retries; attempt++) {
      const attemptStartedAt = performance.now();
      try {
        const imported = await config.importComponent({ name: componentName, root });
        const ok = !!imported;
        markImportResult(componentName, ok);
        trace?.importDone(componentName, ok, performance.now() - attemptStartedAt);
        return ok;
      } catch (err) {
        if (attempt === retries) {
          console.error(`Error importing component '${componentName}':`, err);
          markImportResult(componentName, false);
          trace?.importDone(componentName, false, performance.now() - attemptStartedAt);
          return false;
        }
        await waitMs(100 * 1.5 ** attempt);
      }
    }
    markImportResult(componentName, false);
    return false;
  })();

  pendingImportPromiseByScopeKey.set(scopeKey, importTask);
  const result = await importTask;
  pendingImportPromiseByScopeKey.delete(scopeKey);
  return result;
};

// ─── DOM Observer ────────────────────────────────────────────────────────────

const scheduleDeferredScan = (observerKey, componentName, config, root) => {
  clearTimeout(deferredScanTimerByKey.get(observerKey));
  deferredScanTimerByKey.set(
    observerKey,
    setTimeout(() => {
      deferredScanTimerByKey.delete(observerKey);
      scheduleComponentLoad(componentName, config, { root }, null, null);
    }, 250),
  );
};

const observeDomForComponent = (componentName, config, options) => {
  if (!("MutationObserver" in window) || !config?.componentClass) {
    return;
  }

  const root = options?.root || document;
  const observerKey = `${componentName}::${getRootKey(root)}`;

  if (observedDomScopes.has(observerKey)) {
    return;
  }

  const selector = getSelectorForComponent(componentName, config);
  if (!selector) {
    return;
  }

  observedDomScopes.add(observerKey);

  let mutationScheduled = false;

  const scheduleIdle = (callback) => (typeof window.requestIdleCallback === "function" ? window.requestIdleCallback(callback, { timeout: 250 }) : window.requestAnimationFrame(callback));

  new MutationObserver(() => {
    if (mutationScheduled) {
      return;
    }
    mutationScheduled = true;
    scheduleIdle(() => {
      try {
        if (hasPendingImport(componentName, root) || wasRecentlyImported(componentName)) {
          scheduleDeferredScan(observerKey, componentName, config, root);
          return;
        }
        scheduleComponentLoad(componentName, config, { root }, null, null);
      } catch (err) {
        console.error(`MutationObserver error for '${componentName}':`, err);
      } finally {
        mutationScheduled = false;
      }
    });
  }).observe(root, { childList: true, subtree: true });
};

// ─── Schedule ────────────────────────────────────────────────────────────────

const scheduleComponentLoad = (componentName, config, options, trace, sharedPreloadMargin) => {
  const root = options?.root || document;

  if (!config || typeof config.componentClass !== "function") {
    importComponentNow(componentName, config, options, 5, trace);
    return;
  }

  const preloadMargin = Number.isFinite(options?.preloadMargin) ? options.preloadMargin : Number.isFinite(sharedPreloadMargin) ? sharedPreloadMargin : computePreloadMargin(options);

  if (config.enableObserver && !window.Granite?.author) {
    observeDomForComponent(componentName, config, options);
  }

  const selector = getSelectorForComponent(componentName, config);
  if (!selector) {
    importComponentNow(componentName, config, options, 5, trace);
    return;
  }

  const allElements = root.querySelectorAll(selector);
  if (!allElements.length) {
    trace?.skip(componentName, "Not Present (0 elements)");
    return;
  }

  const scheduledElements = getScheduledElementsSet(componentName);
  const newElements = Array.from(allElements).filter((el) => !scheduledElements.has(el));
  newElements.forEach((el) => scheduledElements.add(el));

  if (!newElements.length) {
    trace?.skip(componentName, "No new elements to schedule");
    return;
  }

  let didRun = false;
  const intentCleanupCallbacks = [];
  const runOnce = () => {
    if (didRun) {
      return;
    }
    didRun = true;
    intentCleanupCallbacks.forEach((cleanup) => cleanup());
    importComponentNow(componentName, config, { root, ignoreIsPresent: true }, 5, trace).then((ok) => {
      if (!ok) {
        newElements.forEach((el) => scheduledElements.delete(el));
      }
    });
  };

  const scheduleRunOnce = () => {
    const mobileDelay = isMobileViewport() && Number.isFinite(config.mobileDelay) ? config.mobileDelay : 0;
    const mobileInteractionSelector = isMobileViewport() && typeof config.mobileInteractionSelector === "string" ? config.mobileInteractionSelector : "";

    if (mobileDelay > 0) {
      let timer = null;
      const removeInteractionListeners = () => {
        document.removeEventListener("pointerdown", runOnInteraction, true);
        document.removeEventListener("focusin", runOnInteraction, true);
        document.removeEventListener("keydown", runOnInteraction, true);
      };
      const runDelayed = () => {
        removeInteractionListeners();
        runOnce();
      };
      const runOnInteraction = (event) => {
        if (!event.target?.closest?.(mobileInteractionSelector)) {
          return;
        }
        clearTimeout(timer);
        runDelayed();
      };

      if (mobileInteractionSelector) {
        document.addEventListener("pointerdown", runOnInteraction, true);
        document.addEventListener("focusin", runOnInteraction, true);
        document.addEventListener("keydown", runOnInteraction, true);
      }

      timer = setTimeout(runDelayed, mobileDelay);
      return;
    }

    runOnce();
  };

  newElements.filter(isSwiperIntentElement).forEach((element) => {
    const loadOnIntent = () => runOnce();
    element.addEventListener("pointerenter", loadOnIntent, { once: true, passive: true });
    element.addEventListener("focusin", loadOnIntent, { once: true });
    element.addEventListener("touchstart", loadOnIntent, { once: true, passive: true });
    intentCleanupCallbacks.push(() => {
      element.removeEventListener("pointerenter", loadOnIntent);
      element.removeEventListener("focusin", loadOnIntent);
      element.removeEventListener("touchstart", loadOnIntent);
    });
  });

  if (config.ignoreViewport) {
    scheduleRunOnce();
    return;
  }

  const hasHiddenElements = newElements.some((el) => !el.getClientRects?.().length);
  if (hasHiddenElements) {
    scheduleRunOnce();
    return;
  }

  if ("IntersectionObserver" in window) {
    const ioRecord = getSharedIntersectionObserver(root, preloadMargin);
    for (const el of newElements) {
      const existingCallbacks = ioRecord.callbacks.get(el);
      if (existingCallbacks) {
        existingCallbacks.push(scheduleRunOnce);
      } else {
        ioRecord.callbacks.set(el, [scheduleRunOnce]);
      }
      ioRecord.count++;
      ioRecord.io.observe(el);
    }
    return;
  }

  runOnce();
};

// ─── Public API ──────────────────────────────────────────────────────────────

const BarceloComponents = {
  init: async (componentsByName, options = {}) => {
    const root = options?.root || document;
    const componentNames = componentsByName ? Object.keys(componentsByName) : [];
    const trace = createLoadTrace(options);
    const sharedMargin = Number.isFinite(options?.preloadMargin) ? options.preloadMargin : computePreloadMargin(options);
    const TIME_BUDGET_MS = 10;

    let index = 0;

    while (index < componentNames.length) {
      const loopStartedAt = performance.now();

      while (index < componentNames.length) {
        const componentName = componentNames[index++];
        const config = componentsByName[componentName];

        try {
          const isPresentFailing = config && typeof config.isPresent === "function" && !config.isPresent(options);

          if (!config || isPresentFailing) {
            componentLoadStatusByName[componentName] = false;
            setManifestEntry(componentName, false);
            trace?.skip(componentName, config ? "isPresent check failed" : "Missing Component Config");

            if (config?.enableObserver && typeof config?.componentClass === "function" && !window.Granite?.author) {
              observeDomForComponent(componentName, config, { root });
            }

            continue;
          }

          if (typeof config.importComponent !== "function") {
            componentLoadStatusByName[componentName] = false;
            setManifestEntry(componentName, false);
            trace?.skip(componentName, "Invalid Config (no importComponent)");
            continue;
          }

          if (options.forceImport || config.forceImport) {
            await importComponentNow(componentName, config, { root, ignoreIsPresent: true }, 5, trace);
          } else {
            scheduleComponentLoad(componentName, config, { root, preloadMargin: sharedMargin }, trace, sharedMargin);
          }
        } catch (err) {
          console.error(`Init error in '${componentName}':`, err);
          componentLoadStatusByName[componentName] = false;
          setManifestEntry(componentName, false);
          trace?.failed(componentName, `Init Error: ${err?.message || "Unknown"}`);
        }

        if (didExceedTimeBudget(loopStartedAt, TIME_BUDGET_MS)) {
          break;
        }
      }

      if (index < componentNames.length) {
        await yieldToBrowser();
      }
    }

    trace?.finish();
  },
};

export default BarceloComponents;
